import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'hooks'
import { fetchSession, fetchSessionMessages, fetchSessions } from '../services/dispatchHelpApi'
import { ChatThread } from '../components/ChatThread'
import { SessionList } from '../components/SessionList'
import {
    appendSessions,
    markRead,
    resetSessionsWindow,
    setActiveSession,
    setLoadingMessages,
    setLoadingMoreSessions,
    setLoadingSessions,
    setMessages,
    setSessions,
    upsertSession,
} from '../store/dispatchHelpSlice'
import { clearAllReplyRefetch, scheduleReplyRefetch } from '../store/socket-handlers'

export function DispatchHelpPage() {
    const dispatch = useAppDispatch()
    const activeSessionId = useAppSelector((s) => s.dispatchHelp.activeSessionId)
    const sessionsById = useAppSelector((s) => s.dispatchHelp.sessions)
    const sessionsCursor = useAppSelector((s) => s.dispatchHelp.sessionsCursor)
    const hasMoreSessions = useAppSelector((s) => s.dispatchHelp.hasMoreSessions)
    const [searchParams, setSearchParams] = useSearchParams()
    const [search, setSearch] = useState('')
    const [status, setStatus] = useState<'all' | 'active'>('all')

    // Monotonic request id — only the latest filter's page may apply. A load-more already in flight
    // when the filter changes is dropped on return instead of appending the old filter's rows.
    const requestSeqRef = useRef(0)
    const inFlightRef = useRef(false)

    // Cancel any pending staggered refetch timers when leaving the page.
    useEffect(() => () => clearAllReplyRefetch(), [])

    // Load the FIRST page on mount + whenever the filter changes (search debounced). Replaces the list
    // and resets the keyset cursor.
    useEffect(() => {
        let cancelled = false
        // Synchronously, BEFORE the debounce: the cursor still points into the previous filter's result
        // set, and load-more is cursor-gated, so dropping it here closes the whole debounce window.
        requestSeqRef.current += 1
        dispatch(resetSessionsWindow())
        const run = async () => {
            dispatch(setLoadingSessions(true))
            try {
                const page = await fetchSessions({ status, search: search.trim() || undefined })
                if (!cancelled) dispatch(setSessions({ sessions: page.sessions, ...page.pagination }))
            } finally {
                if (!cancelled) dispatch(setLoadingSessions(false))
            }
        }
        const t = setTimeout(run, search ? 300 : 0)
        return () => {
            cancelled = true
            clearTimeout(t)
        }
    }, [dispatch, status, search])

    // Next (older) page — driven by the list scrolling near its end. The cursor is a keyset marker, so
    // consecutive calls walk backwards through history without OFFSET drift.
    const loadMoreSessions = useCallback(async () => {
        // Ref, not the `loadingMoreSessions` flag: a scroll fling fires several events before React
        // re-renders, so the flag still reads false on the second one and the same page is fetched twice.
        // A null cursor parks load-more across the whole filter-change window (see resetSessionsWindow).
        if (!hasMoreSessions || inFlightRef.current || !sessionsCursor) return
        inFlightRef.current = true
        const seq = requestSeqRef.current
        dispatch(setLoadingMoreSessions(true))
        try {
            const page = await fetchSessions({ status, search: search.trim() || undefined, before: sessionsCursor })
            if (seq === requestSeqRef.current) dispatch(appendSessions({ sessions: page.sessions, ...page.pagination }))
        } finally {
            inFlightRef.current = false
            dispatch(setLoadingMoreSessions(false))
        }
    }, [dispatch, hasMoreSessions, sessionsCursor, status, search])

    async function onSelect(tabId: string) {
        dispatch(setActiveSession(tabId))
        dispatch(markRead(tabId))
        // The thread renders off the session ROW, not the id. A session the loaded page doesn't hold —
        // the deep-link target is usually older than the first page — has to be fetched on its own, or
        // the pane sits on "Select a session" with the session open.
        if (!sessionsById[tabId]) {
            try {
                const row = await fetchSession(tabId)
                if (row) dispatch(upsertSession(row))
            } catch {
                // 404 / not an operator for this tenant — leave the empty state, the list still works.
            }
        }
        // Reflect the open session in the URL: shareable + refresh-safe, and the landing target for the
        // Telegram "Join Conversation" deep link (?session=<tabId>).
        setSearchParams({ session: tabId }, { replace: true })
        dispatch(setLoadingMessages(true))
        try {
            const { messages, total } = await fetchSessionMessages(tabId)
            dispatch(setMessages({ tabId, messages, total }))
            // The first AI reply may still be streaming (we don't get agent chunks) — pull it in
            // shortly after opening so it isn't missed until the next user turn.
            scheduleReplyRefetch(tabId, dispatch)
        } finally {
            dispatch(setLoadingMessages(false))
        }
    }

    // Mobile back: drop the open session so the list pane shows again.
    function onBack() {
        dispatch(setActiveSession(null))
        setSearchParams({}, { replace: true })
    }

    // Deep link: open ?session=<tabId> on first load (from the Telegram "Join Conversation" button).
    useEffect(() => {
        const sid = searchParams.get('session')
        if (sid) void onSelect(sid)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <div className="flex h-full overflow-hidden bg-white dark:bg-slate-900">
            {/* Master-detail: on mobile show ONE pane — the list until a session is open, then the thread
                (with a back button); on md+ both panes sit side by side. */}
            <div className={`${activeSessionId ? 'hidden md:block' : 'block'} w-full shrink-0 md:w-72`}>
                <SessionList
                    search={search}
                    status={status}
                    onSearchChange={setSearch}
                    onStatusChange={setStatus}
                    onSelect={onSelect}
                    onLoadMore={loadMoreSessions}
                />
            </div>
            {/* On mobile an open conversation goes full-screen below the header bar (which keeps the nav
                burger), so it covers the sidebar and pins the composer to the bottom; on md+ it's the
                right pane. */}
            <div
                className={`${
                    activeSessionId
                        ? 'fixed inset-x-0 bottom-0 top-[var(--Header-height)] z-50 bg-white dark:bg-slate-900 md:static md:top-auto md:z-auto'
                        : 'hidden md:block'
                } min-w-0 flex-1`}
            >
                <ChatThread onBack={onBack} onSelectSession={onSelect} />
            </div>
        </div>
    )
}
